// scheduling/components/pool/SendToPoolModal.jsx
// Confirm-gate for releasing a job into a preparer pool (Job Pools, Trello #257).
//
// Reuses the existing scheduler modal vocabulary (.sched-mgr-modal*) rather than
// the POC's inline styles, so it matches PlaceholderAssignModal and the rest of
// the app.
//
// Three things this modal is responsible for:
//
//  1. Pool choice — locked to the 990 pool for any 990 and to ETF for
//     Estate/Trust/Foreign work (§2.1), a GEN vs EXPTAX choice otherwise.
//  2. The 20h advisory (§2.2) — a warning, never a block. Pools are for small
//     jobs, but the manager decides.
//  3. The clearing gate — the only destructive write in the feature. On open we
//     ask the server what clearing WOULD remove and show it verbatim. Clearing is
//     OPT-IN (unchecked by default) and the confirm call re-derives the set
//     server-side; the client never sends entry ids.
//
// There is no undo: once a job is pooled, PE has been written and the manager has
// relinquished preparer choice (§2.2). Hence the explicit confirm.

import { useEffect, useState } from 'react';
import { POOL_META, allowedPoolsForJob, lockedPoolForJob, defaultPoolForJob } from '../../utils/poolEligibility';
import { previewSendToPool, sendToPool } from '../../services/poolApi';

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return `${dt.getMonth() + 1}/${dt.getDate()}`;
};
const fmtHours = (h) => {
  const n = Number(h) || 0;
  return (Math.round(n * 100) / 100).toString();
};

export default function SendToPoolModal({ job, onClose, onSent }) {
  const locked = lockedPoolForJob(job);
  const allowed = allowedPoolsForJob(job);

  const [poolCode, setPoolCode] = useState(defaultPoolForJob(job));
  const [notes, setNotes] = useState('');
  const [clearEntries, setClearEntries] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Ask the server what's at stake before showing any confirm affordance. The
  // preview also carries the advisory text and the authoritative allowed-pool
  // list, so the modal never has to duplicate those rules.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    previewSendToPool({ jobId: job.JobId, poolCode })
      .then(r => { if (alive) { setPreview(r); setLoading(false); } })
      .catch(e => { if (alive) { setError(e.message); setLoading(false); } });
    return () => { alive = false; };
    // Re-previewing per pool choice is cheap and keeps the advisory in step.
  }, [job.JobId, poolCode]);

  const clearing = preview?.clearing;
  const hasEntries = !!clearing && clearing.entryCount > 0;
  // Admin staff may send a job they don't lead, but clearing its scheduled hours stays
  // with the job's manager/partner (the server refuses it otherwise). They still see
  // what's scheduled, so nobody pools a job without knowing time is booked against it.
  const canClear = preview?.canClear !== false;

  async function handleConfirm() {
    setSubmitting(true);
    setError(null);
    try {
      const res = await sendToPool({ jobId: job.JobId, poolCode, notes, clearEntries });
      onSent?.(res);
      onClose();
    } catch (e) {
      setError(e.message);
      setSubmitting(false);
    }
  }

  return (
    <div className="sched-mgr-modal-backdrop" onClick={onClose}>
      <div className="sched-mgr-modal" onClick={e => e.stopPropagation()}>
        <div className="sched-mgr-modal-header">
          <h3>Send to Pool</h3>
          <button className="sched-mgr-sidebar-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <div className="sched-mgr-modal-job">
          <span className="sched-mgr-modal-job-name">{job.Name}</span>
          <span className="sched-mgr-modal-job-client">{job.ClientName}</span>
          <span className="sched-mgr-modal-job-budget">
            {job.ServiceDesc || job.JobType || 'General'}
            {job.BudgetHours ? ` · ${fmtHours(job.BudgetHours)}h budget` : ''}
            {job.Deadline ? ` · due ${fmtDate(job.Deadline)}` : ''}
          </span>
        </div>

        {/* ── Pool choice ── */}
        <div className="sched-mgr-modal-field">
          <label className="sched-mgr-modal-label">Pool</label>
          {locked ? (
            <div className="sched-pool-locked">
              <span className="sched-pool-chip" style={{ background: POOL_META[locked].color }}>
                {POOL_META[locked].label}
              </span>
              <span className="sched-pool-locked-note">
                {locked === 'NFP'
                  ? '990 returns always route here — no choice to make.'
                  : 'Estate / Trust / Foreign work always routes here — no choice to make.'}
              </span>
            </div>
          ) : (
            <div className="sched-pool-choice">
              {allowed.map(code => (
                <button
                  key={code}
                  type="button"
                  className={`sched-mgr-modal-btn sched-pool-choice-btn${poolCode === code ? ' sched-pool-choice-btn--on' : ''}`}
                  onClick={() => setPoolCode(code)}
                  disabled={submitting}
                >
                  <span className="sched-pool-choice-label">{POOL_META[code].label}</span>
                  <span className="sched-pool-choice-blurb">{POOL_META[code].blurb}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Advisory (never blocks) ── */}
        {preview?.advisory && (
          <div className="sched-pool-advisory">⚠ {preview.advisory}</div>
        )}

        {/* ── Notes ── */}
        <div className="sched-mgr-modal-field">
          <label className="sched-mgr-modal-label">Notes / context (optional)</label>
          <textarea
            className="sched-pool-notes"
            rows={3}
            maxLength={500}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Why this pool? e.g. &ldquo;Client has foreign income — needs an experienced preparer&rdquo;"
            disabled={submitting}
          />
          <div className="sched-pool-notes-hint">
            Shown on the job card in the pool, included in the PE notification, and editable later.
          </div>
        </div>

        {/* ── Clearing gate ── */}
        {loading && <div className="sched-pool-loading">Checking existing schedule…</div>}

        {!loading && hasEntries && (
          <div className="sched-pool-clearing">
            <div className="sched-pool-clearing-head">
              This job has {clearing.entryCount} scheduled {clearing.entryCount === 1 ? 'entry' : 'entries'}
              {' '}({fmtHours(clearing.totalHours)}h) from today onward
            </div>
            <ul className="sched-pool-clearing-list">
              {clearing.affectedStaff.map(s => (
                <li key={s.staffId}>
                  <strong>{s.staffName}</strong> — {fmtHours(s.hours)}h across {s.entries}
                  {' '}{s.entries === 1 ? 'day' : 'days'} ({fmtDate(s.firstDate)}–{fmtDate(s.lastDate)})
                </li>
              ))}
            </ul>
            {canClear ? (
              <label className="sched-pool-clearing-toggle">
                <input
                  type="checkbox"
                  checked={clearEntries}
                  onChange={e => setClearEntries(e.target.checked)}
                  disabled={submitting}
                />
                <span>
                  Clear this scheduled time when pooling
                  <span className="sched-pool-clearing-warn">
                    {' '}— deletes those entries and notifies the staff. This can&rsquo;t be undone.
                  </span>
                </span>
              </label>
            ) : (
              <div className="sched-pool-clearing-keep">
                Only the job&rsquo;s manager or partner can clear this time. The job will go to the
                pool with it still on the calendar — let them know if it should come off.
              </div>
            )}
            {canClear && !clearEntries && (
              <div className="sched-pool-clearing-keep">
                Leaving it as-is: the job goes to the pool but the scheduled time stays on the calendar.
              </div>
            )}
          </div>
        )}

        {/* Nothing rendered when the job has no today-onward entries — an already
            unscheduled job has nothing to clear, so the whole gate stays out of the
            way rather than reassuring you about a non-problem. */}

        {error && <div className="sched-pool-error">{error}</div>}

        <div className="sched-pool-actions">
          <button
            type="button"
            className="sched-mgr-modal-btn sched-pool-btn-cancel"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="sched-mgr-modal-btn sched-pool-btn-confirm"
            onClick={handleConfirm}
            disabled={submitting || loading || !!error}
          >
            {submitting ? 'Sending…' : `Send to ${POOL_META[poolCode]?.label || poolCode}`}
          </button>
        </div>

        <div className="sched-pool-footnote">
          Once pooled, any eligible staffer can claim this job and it leaves its manager&rsquo;s
          My Jobs list — the manager no longer chooses the preparer.
        </div>
      </div>
    </div>
  );
}
